import React, { useMemo, useState } from 'react';
import * as _ from 'lodash';
import { Tag } from '@americanexpress/dls-react';
import { FilterCategory, FilterMenu } from 'dls-react-filters';
import { TransactionType } from 'src/types/transaction-type.enum';
import { useCategories } from 'src/hooks/queries/categories';
import { useVisibleAccounts } from 'src/hooks/queries/accounts';
import CategoryService from 'src/services/category.service';
import styles from './TransactionFilter.module.scss';
import { TransactionFilterMenu } from 'src/types/transaction-filter-menu.enum';
import {
    DateFilterOption,
    SelectedFilterOptions,
    TransactionFilterOption,
    TypeFilterOption
} from 'src/types/transaction-filter-option.model';
import LiteralsService from 'src/services/literals/LiteralsService';
import { useSearchParams } from 'src/hooks/useSearchParams';
import DateUtils from 'src/utils/date';
import { CategoryModel } from 'src/types/category.model';
import { AccountModel } from 'src/types/account.model';
import { useBreakpoints } from 'src/hooks/useBreakpoints';
import FormatService from 'src/services/format.service';
import { LocalDate } from '@js-joda/core';
import { trackClick } from 'src/hooks/useAdobeTags';
import { useLocation } from 'react-router-dom';
import adobeTags from 'src/types/adobe-tags';
import AccountService from 'src/services/account.service';
import Selectors from 'src/utils/selectors';
import DateRangeModal from 'src/components/atomic/organisms/transaction/custom-date-modal/DateRangeModal';
import FilterSubMenu from 'src/components/atomic/organisms/transaction/filters/FilterSubMenu';
import { getCategoryTagName } from 'src/utils/category.utils';
import { getAccountTagName } from 'src/utils/accounts.utils';

interface TransactionFilterProps {
    tagBreak?: boolean;
    children?: JSX.Element;
}

interface TransactionFilterCategory {
    id: TransactionFilterMenu;
    name: string;
    longName?: string;
}

type FilterOptionsByCategory = {
    [menu in TransactionFilterMenu]?: TransactionFilterOption[];
};

const filterCategories: TransactionFilterCategory[] = [
    { id: TransactionFilterMenu.DATE, name: LiteralsService.getLiteral('TransactionsView.filters.date.title') },
    { id: TransactionFilterMenu.TYPE, name: LiteralsService.getLiteral('TransactionsView.filters.type.title') },
    { id: TransactionFilterMenu.CATEGORY, name: LiteralsService.getLiteral('TransactionsView.filters.category.title') },
    { id: TransactionFilterMenu.ACCOUNT, name: LiteralsService.getLiteral('TransactionsView.filters.account.title') }
];

const dateOptions: DateFilterOption[] = [
    {
        id: 'yesterday',
        name: LiteralsService.getLiteral('TransactionsView.filters.date.yesterday'),
        start: DateUtils.daysAgo(1),
        end: LocalDate.now()
    },
    {
        id: 'lastWeek',
        name: LiteralsService.getLiteral('TransactionsView.filters.date.lastWeek'),
        start: DateUtils.daysAgo(7),
        end: DateUtils.daysAhead(1)
    },
    {
        id: 'currentMonth',
        name: LiteralsService.getLiteral('TransactionsView.filters.date.currentMonth'),
        start: DateUtils.startDateOfThisMonth(),
        end: DateUtils.startDateOfNextMonth()
    },
    {
        id: 'lastMonth',
        name: LiteralsService.getLiteral('TransactionsView.filters.date.lastMonth'),
        start: DateUtils.startDateOfLastMonth(),
        end: DateUtils.startDateOfThisMonth()
    },
    {
        id: 'last12Months',
        name: LiteralsService.getLiteral('TransactionsView.filters.date.lastTwelveMonths'),
        start: DateUtils.yearsAgo(1),
        end: DateUtils.daysAhead(1)
    },
    {
        id: 'lastYear',
        name: LiteralsService.getLiteral('TransactionsView.filters.date.lastYear'),
        start: DateUtils.startDateOfLastYear(),
        end: DateUtils.startDateOfThisYear()
    },
    {
        id: 'customDate',
        name: LiteralsService.getLiteral('TransactionsView.filters.date.custom'),
        start: undefined,
        end: undefined
    }
];

const customDateId: string = 'customDate';

const typeOptions: TypeFilterOption[] = [
    { id: TransactionType.TRANSACTION_EXPENSE, name: 'Expense' },
    { id: TransactionType.TRANSACTION_INCOME, name: 'Income' },
    { id: TransactionType.TRANSACTION_INTERNAL_TRANSFER, name: 'Transfer' }
];

function mapSearchParamsToFilterOptions(
    searchParams: URLSearchParams,
    categoryOptions?: CategoryModel[],
    accountOptions?: AccountModel[]
): SelectedFilterOptions {
    return useMemo(() => {
        const startDate: LocalDate = DateUtils.dateify(searchParams.get('start'));
        const endDate: LocalDate = DateUtils.dateify(searchParams.get('end'));
        const typeId: TransactionType = searchParams.get('type') as TransactionType;
        const categoryId: string = searchParams.get('group');
        const accountId: string = searchParams.get('account');

        const selectedFilterOptions = {} as SelectedFilterOptions;

        if (startDate && endDate) {
            const selectedDateOption = dateOptions.find((dateOption) => {
                return startDate.equals(dateOption.start) && endDate.equals(dateOption.end);
            });
            if (selectedDateOption) selectedFilterOptions[TransactionFilterMenu.DATE] = selectedDateOption;
            else if (startDate.dayOfMonth() === 1 && endDate.equals(startDate.plusMonths(1))) {
                /* If the date range encompasses a month it is a custom date option.
                     Note the name will display in the parent menu and in the tag */
                selectedFilterOptions[TransactionFilterMenu.DATE] = {
                    id: customDateId,
                    name: FormatService.formatAltDateLongMonth(startDate),
                    start: startDate,
                    end: endDate
                };
            } else {
                // If the dates do not map to any of the options it is a custom date range option
                selectedFilterOptions[TransactionFilterMenu.DATE] = {
                    id: customDateId,
                    name: LiteralsService.getLiteral('TransactionsView.filters.date.custom'),
                    start: startDate,
                    end: endDate
                };
            }
        }

        if (typeId) {
            const selectedType = typeOptions.find((typeOption) => typeOption.id === typeId);
            if (selectedType) {
                selectedFilterOptions[TransactionFilterMenu.TYPE] = selectedType;
            }
        }

        if (categoryOptions && categoryId) {
            const selectedCategory = categoryOptions.find((categoryOption) => categoryOption.id === categoryId);
            if (selectedCategory) {
                selectedFilterOptions[TransactionFilterMenu.CATEGORY] = selectedCategory;
            }
        }

        if (accountOptions && accountId) {
            const selectedAccount = accountOptions.find((accountOption) => accountOption.id.toString() === accountId);
            if (selectedAccount) {
                selectedFilterOptions[TransactionFilterMenu.ACCOUNT] = selectedAccount;
            }
        }

        return selectedFilterOptions;
    }, [searchParams, categoryOptions, accountOptions]);
}

function mapFilterOptionsToParams(filterOptions: SelectedFilterOptions, searchParams: URLSearchParams): void {
    const dateOption = filterOptions.dateMenu as DateFilterOption;
    const typeOption = filterOptions.typeMenu?.id;
    const categoryOption = filterOptions.categoryMenu?.id;
    const accountOption = filterOptions.accountMenu?.id;

    dateOption ? searchParams.set('start', dateOption?.start.toString()) : searchParams.delete('start');
    dateOption ? searchParams.set('end', dateOption?.end.toString()) : searchParams.delete('end');
    typeOption ? searchParams.set('type', typeOption.toString()) : searchParams.delete('type');
    categoryOption ? searchParams.set('group', categoryOption.toString()) : searchParams.delete('group');
    accountOption ? searchParams.set('account', accountOption.toString()) : searchParams.delete('account');
}

export default function TransactionFilter({ tagBreak, children }: TransactionFilterProps): JSX.Element {
    const [searchParams, setSearchParams] = useSearchParams();
    const { isMinWidthLarge } = useBreakpoints();
    const { pathname } = useLocation();

    const { data: categories, isLoading: isLoadingCategories } = useCategories();
    const { data: accountOptions, isLoading: isLoadingAccounts } = useVisibleAccounts();
    const [currentMenu, setCurrentMenu] = useState(undefined);
    const [selectedCategory, setSelectedCategory] = useState('');
    const [showCustomDateModal, setShowCustomDateModal] = useState(false);

    const parentFilterId = 'parent-filter';
    const categoryOptions = categories
        ? CategoryService.getCategoryOptionsWithNoCustomSubcategory(categories)
        : undefined;

    const selectedOptions = mapSearchParamsToFilterOptions(searchParams, categoryOptions, accountOptions);

    if (isLoadingCategories || isLoadingAccounts) return null;

    const optionsByCategory: FilterOptionsByCategory = {
        [TransactionFilterMenu.DATE]: dateOptions,
        [TransactionFilterMenu.TYPE]: typeOptions,
        [TransactionFilterMenu.CATEGORY]: categoryOptions,
        [TransactionFilterMenu.ACCOUNT]: accountOptions
    };

    const resetPage = (): void => {
        searchParams.set('page', '1');
    };

    const handleResetButtonClick = (): void => {
        trackClick(pathname, adobeTags.Filter.Remove);
        setSearchParams({});
    };

    const handleCloseParent = (): void => {
        // need this conditional due to the difference in the way events are attached in react 16 (DLS)
        // vs react 17 (bw-react)
        if (currentMenu === TransactionFilterMenu.PARENT) {
            setCurrentMenu(undefined);
        }
    };
    const handleBackToParent = (): void => {
        setCurrentMenu(TransactionFilterMenu.PARENT);
    };
    const handleFilterCategoryClick = (id: string, categoryName: string): void => {
        setCurrentMenu(id);
        setSelectedCategory(FormatService.formatTag(categoryName));
    };
    const handleOptionClick = (menu: TransactionFilterMenu, option: TransactionFilterOption): void => {
        const newOptions: SelectedFilterOptions = selectedOptions;
        newOptions[menu] = option;
        resetPage();
        mapFilterOptionsToParams(newOptions, searchParams);
        setSearchParams(searchParams);
        setCurrentMenu(undefined);
        let displayName: string = '';
        if (menu === TransactionFilterMenu.CATEGORY)
            displayName = getCategoryTagName(option as CategoryModel, categories);
        if (menu === TransactionFilterMenu.ACCOUNT) displayName = getAccountTagName(option as AccountModel);
        const fullTagName: string = adobeTags.Filter.Select + selectedCategory + '>' + displayName;
        trackClick(pathname, fullTagName);
    };

    const removeSelectedOption = (menu: TransactionFilterMenu): void => {
        const newOptions = _.omit(selectedOptions, menu);
        resetPage();
        mapFilterOptionsToParams(newOptions, searchParams);
        setSearchParams(searchParams);
        trackClick(pathname, adobeTags.Filter.Remove);
    };

    const countAppliedFilters = (): number => {
        // DLS will display the green count circle with "0" unless we specifically pass in
        // undefined when the filter count is 0
        return _.keys(selectedOptions).length || undefined;
    };

    const getSelectedText = (menu: TransactionFilterMenu): string => {
        if (menu === TransactionFilterMenu.ACCOUNT && selectedOptions[menu])
            return AccountService.getDisplayName(selectedOptions[menu] as AccountModel);
        if (
            menu === TransactionFilterMenu.DATE &&
            (selectedOptions[menu] as DateFilterOption)?.name ===
                LiteralsService.getLiteral('TransactionsView.filters.date.custom')
        ) {
            const startDate: LocalDate = (selectedOptions[menu] as DateFilterOption).start;
            const endDate: LocalDate = (selectedOptions[menu] as DateFilterOption).end.minusDays(1);
            return `${FormatService.formatDate(startDate)} - ${FormatService.formatDate(endDate)}`;
        }
        return selectedOptions[menu]?.name || 'All';
    };

    const isDisabled = (menu: TransactionFilterMenu): boolean => {
        return accountOptions.length === 0 && menu === TransactionFilterMenu.ACCOUNT;
    };

    const getCustomDate = (): void => {
        setShowCustomDateModal(true);
    };

    const customDateModal: JSX.Element = showCustomDateModal && (
        <DateRangeModal
            closeModal={(startDate?: LocalDate, endDate?: LocalDate) => {
                if (startDate && endDate) {
                    const selectedDateRange: DateFilterOption = {
                        id: customDateId,
                        name: LiteralsService.getLiteral('TransactionsView.filters.date.custom'),
                        start: startDate,
                        end: endDate.plusDays(1)
                    };
                    handleOptionClick(TransactionFilterMenu.DATE, selectedDateRange);
                }
                setShowCustomDateModal(false);
            }}
        />
    );

    return (
        <div
            className={`${styles.filterContainer} ${tagBreak ? styles.tagBreak : ''}`}
            data-cy={Selectors.Transactions.FILTER_CONTAINER}
        >
            <div>
                {children}
                <FilterMenu
                    id={parentFilterId}
                    data-cy={Selectors.Transactions.FILTER_MENU}
                    showMenu={currentMenu === TransactionFilterMenu.PARENT}
                    onFilterButtonClick={() => {
                        trackClick(pathname, adobeTags.Filter.Icon);
                        setCurrentMenu(TransactionFilterMenu.PARENT);
                    }}
                    onClose={handleCloseParent}
                    onResetButtonClick={handleResetButtonClick}
                    filtersLabel={LiteralsService.getLiteral('TransactionsView.filters.parent.filterBy')}
                    filtersApplied={countAppliedFilters()}
                    singleSelect
                    selectableOptions={false}
                    lastOptionIndex={filterCategories.length - 1}
                    align={isMinWidthLarge ? 'left' : 'right'}
                    mobileGutterWidth="44px"
                >
                    {filterCategories.map((category) => (
                        <FilterCategory
                            key={category.id}
                            id={category.id}
                            data-category={category.longName || category.name}
                            selectedText={getSelectedText(category.id)}
                            onClick={() => handleFilterCategoryClick(category.id, category.longName || category.name)}
                            disabled={isDisabled(category.id)}
                        >
                            {category.longName || category.name}
                        </FilterCategory>
                    ))}
                </FilterMenu>
                {filterCategories.map((category) => (
                    <FilterSubMenu
                        key={`${category.id}-sub-menu`}
                        parentFilterId={parentFilterId}
                        menuKey={category.id}
                        menuName={category.name}
                        showMenu={currentMenu === category.id}
                        options={optionsByCategory[category.id]}
                        onClose={handleBackToParent}
                        onResetButtonClick={handleResetButtonClick}
                        selectedOptions={selectedOptions}
                        handleOptionClick={(menu, option) => {
                            if (option.id === customDateId) {
                                getCustomDate();
                            } else {
                                handleOptionClick(menu, option);
                            }
                        }}
                        align={isMinWidthLarge ? 'left' : 'right'}
                        searchable={category.id === TransactionFilterMenu.CATEGORY}
                    />
                ))}
            </div>
            <div className={styles.selectedFilterTags}>
                {filterCategories.map((category) => {
                    return (
                        selectedOptions[category.id] && (
                            <Tag
                                key={`${category.id}-tag`}
                                dismissible
                                onDismiss={() => removeSelectedOption(category.id)}
                                data-cy={Selectors.Transactions.FILTER_TAG}
                            >
                                {getSelectedText(category.id)}
                            </Tag>
                        )
                    );
                })}
            </div>
            {customDateModal}
        </div>
    );
}
